/**
 * Product Price Component
 *
 * Custom element that displays product prices with discount support.
 *
 * Two sources of discount data (in order of priority):
 * 1. MysaEvents.buyboxDiscountUpdate event from discount-manager (on buy pages)
 * 2. Server-side Liquid calculation (fallback for no-JS)
 *
 * Discount calculation:
 * - Sitewide/launch: Calculated server-side by Liquid from metafields
 * - Custom-price: Calculated server-side by Liquid
 * - Utility discounts: Calculated client-side by utility-price-handler
 *
 * @module MysaProductPrice
 */

import { Component } from "@theme/component";
import {
  findSectionId,
  formatPrice,
  applyUtilityDiscountWhenReady,
  setupUtilityListeners,
  syncPriceSaleSticker,
} from "@theme/utility-price-handler";
import { MysaEvents } from "@theme/mysa-events";

/**
 * @typedef {Object} ProductPriceRefs
 * @property {HTMLElement} [priceWrapper] - The price display wrapper
 * @property {HTMLElement} [priceAmount] - The main price amount element
 * @property {HTMLElement} [priceCompare] - The compare-at price element (optional)
 */

/**
 * Product price custom element that handles discount display updates
 * @extends {Component<ProductPriceRefs>}
 */
class MysaProductPrice extends Component {
  /** @type {AbortController | null} */
  #abortController = null;

  /** @type {(() => void) | null} */
  #cleanupUtilityListeners = null;

  connectedCallback() {
    super.connectedCallback();

    this.handle = this.dataset.productHandle;

    // Read discounted price from wrapper (set by mysa-price-display.liquid)
    const priceWrapper = this.refs.priceWrapper;
    const calculatedPrice = priceWrapper?.dataset?.calculatedPrice;
    const originalPrice = priceWrapper?.dataset?.originalPrice;
    this.originalPrice = parseInt(
      originalPrice || this.dataset.originalPrice || "0",
      10,
    );
    this.discountedPrice = calculatedPrice
      ? parseInt(calculatedPrice, 10)
      : this.originalPrice;

    if (!this.handle || !this.refs.priceAmount) return;

    // Find parent section ID for section rendering
    this.sectionId = findSectionId(this);

    // Create AbortController for event cleanup
    this.#abortController = new AbortController();
    const signal = this.#abortController.signal;

    // Listen for discount updates from the discount manager (buy page)
    document.addEventListener(
      MysaEvents.buyboxDiscountUpdate,
      (event) => this.#onDiscountUpdate(/** @type {CustomEvent} */ (event)),
      { signal },
    );

    this.#cleanupUtilityListeners = /** @type {() => void} */ (
      setupUtilityListeners({
        sectionId: this.sectionId,
        onSectionReloaded: () => this.#applyUtilityDiscount(),
      })
    );

    // Apply utility discount on initial mount if utility is selected
    this.#applyUtilityDiscount();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
    this.#abortController = null;

    if (this.#cleanupUtilityListeners) {
      this.#cleanupUtilityListeners();
      this.#cleanupUtilityListeners = null;
    }
  }

  /**
   * Handle discount update events from the discount manager (buy page)
   * @param {CustomEvent} event - The discount update event
   */
  #onDiscountUpdate(event) {
    const pricing = event.detail;

    // Only update if this event is for our product
    if (pricing.productHandle !== this.handle) return;

    // Calculate per-unit discounted price from total
    const quantity = pricing.quantity || 1;
    const unitPrice = Math.round(pricing.discountedTotal / quantity);
    const hasDiscount = pricing.totalDiscount > 0;
    const utilityAdjusted = pricing.utilityDiscount > 0;

    this.#updateDisplay(unitPrice, hasDiscount, utilityAdjusted);
  }

  /**
   * Apply utility discount on top of server-rendered price
   * Skips if Liquid already applied the utility discount (data-utility-applied="true")
   */
  #applyUtilityDiscount() {
    // Check if Liquid already applied utility discount
    if (this.refs.priceWrapper?.dataset?.utilityApplied === "true") {
      // Utility was already applied server-side, no need for JS to apply again
      // Section will reload with correct prices when utility changes
      return;
    }

    if (!this.handle || this.discountedPrice === undefined) return;

    applyUtilityDiscountWhenReady(
      this.handle,
      this.discountedPrice,
      /**
       * @param {number} discountAmount
       * @param {number | null} priceFloor
       */
      (discountAmount, priceFloor) => {
        let finalPrice = (this.discountedPrice || 0) - discountAmount;

        // Enforce price floor if exists (prevents going below minimum price)
        // This handles race conditions where Liquid applied sitewide before
        // knowing about the utility partner's price floor
        if (priceFloor !== null && finalPrice < priceFloor) {
          finalPrice = priceFloor;
        }

        this.#updateDisplay(finalPrice, true, true);
      },
    );
  }

  /**
   * Update the price display
   * @param {number} price - Price in cents
   * @param {boolean} hasDiscount - Whether there's a discount
   * @param {boolean} utilityAdjusted - Whether utility pricing produced the price
   */
  #updateDisplay(price, hasDiscount, utilityAdjusted = false) {
    const { priceAmount, priceCompare } = this.refs;

    if (!priceAmount) return;

    // Update main price
    priceAmount.textContent = formatPrice(price);

    if (hasDiscount) {
      const originalPrice = this.originalPrice || 0;
      if (!priceCompare) {
        const del = document.createElement("del");
        del.className = "product-price-display__compare";
        del.setAttribute("data-price-compare", "");
        del.setAttribute("ref", "priceCompare");
        del.textContent = formatPrice(originalPrice);
        priceAmount.after(del);
      } else {
        priceCompare.textContent = formatPrice(originalPrice);
        priceCompare.hidden = false;
      }
    } else {
      if (priceCompare) priceCompare.hidden = true;
    }

    syncPriceSaleSticker(this, {
      originalPrice: this.originalPrice,
      finalPrice: price,
      hasDiscount,
      utilityAdjusted,
    });
  }
}

// Register custom element
if (!customElements.get("mysa-product-price")) {
  customElements.define("mysa-product-price", MysaProductPrice);
}
